/**
 * @documental
 * @export SubmitGroup
 */

import { FC, useState, useEffect, useMemo, PropsWithChildren } from "react";
import { withSubComponents } from "../../utilities/withSubComponents";
import { SubmitGroupContext, SubmitGroupContextValue } from "./SubmitGroupContext";
import { Placeholder } from "./Placeholder";
import { useSubmitting } from "./useSubmitting";

/**
 * The `SubmitGroup` component properties.
 */
export interface SubmitGroupProps {
  /**
   * The submitting state. This can either be true/false or a Promise.
   * If a Promise is specified, the submitting group will be in a submitting
   * state until the promise is resolved.
   */
  readonly submitting?: boolean | Promise<unknown>;
}

/**
 * The `SubmitGroup` component sub-components.
 */
export interface SubmitGroupSubComponents {
  /**
   * A component which renders fallback content instead of its children if submitting.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A component which informs children whether a form submit operation is taking place.
 *
 * `import { SubmitGroup } from "./SubmitGroup";`
 *
 * <strong>Example use case:</strong>
 *
 * ```typescript
 * <SubmitGroup {...props}>
    <SubmitButton>Submit</SubmitButton>
  </SubmitGroup>
  ```
 */
export const SubmitGroup = withSubComponents<FC<PropsWithChildren<SubmitGroupProps>>, SubmitGroupSubComponents>(
  {
    Placeholder,
  },
  (props) => {
    const { submitting = false, children } = props;

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const parentLoading = useSubmitting();

    useEffect(() => {
      if (typeof submitting === "boolean") {
        setIsSubmitting(submitting);
        return;
      }

      setIsSubmitting(true);
      submitting.finally(() => setIsSubmitting(false));
    }, [submitting]);

    const contextValue = useMemo(
      (): SubmitGroupContextValue => ({
        submitting: isSubmitting || parentLoading,
      }),
      [isSubmitting, parentLoading],
    );

    return <SubmitGroupContext.Provider value={contextValue}>{children}</SubmitGroupContext.Provider>;
  },
);
